import React, { useEffect, useState } from 'react';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';
import { UserAttachmentInput, CompanyAttachmentTypeView } from '../../../../models/general';
import { getCompanyAttachmentTypes } from '../../../../api/General/attachment';
import { DatePicker as CustomDatePicker } from '../../../../components/dateTimePickers';
import { formatDateForInput } from '../../../../utils';
import { useTranslation } from 'react-i18next';
import { useFileUploadLimits } from '../../../../hooks/useFileUploadLimits';

interface Props {
  attachmentInput: UserAttachmentInput;
  setAttachmentInput: React.Dispatch<React.SetStateAction<UserAttachmentInput>>;
}

const AttachmentInputForm: React.FC<Props> = ({ attachmentInput, setAttachmentInput }) => {
  const { t } = useTranslation('common');
  const { validateFileSize } = useFileUploadLimits();
  const [attachmentTypes, setAttachmentTypes] = useState<CompanyAttachmentTypeView[]>([]);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAttachmentTypes = async () => {
      const response = await getCompanyAttachmentTypes();
      setAttachmentTypes(response.payload);
    };

    fetchAttachmentTypes();
  }, []);

  return (
    <form className="flex flex-col gap-2.5">
      <Input
        label={t("File Name")}
        type="text"
        value={attachmentInput.fileName}
        onChange={(e) => setAttachmentInput({ ...attachmentInput, fileName: e.target.value })}
      />
      <Input
        label={t("Description")}
        type="text"
        value={attachmentInput.description}
        onChange={(e) => setAttachmentInput({ ...attachmentInput, description: e.target.value })}
      />
      <Input
        label={t("documentNumber")}
        type="text"
        value={attachmentInput.documentNumber ?? ''}
        onChange={(e) => setAttachmentInput({ ...attachmentInput, documentNumber: e.target.value })}
      />
      <CustomDatePicker
        label={t("issueDate")}
        value={attachmentInput.issueDate ? new Date(attachmentInput.issueDate) : null}
        onChange={(date) => setAttachmentInput({ ...attachmentInput, issueDate: formatDateForInput(date ?? undefined) })}
      />
      <CustomDatePicker
        label={t("Expiry Date")}
        value={attachmentInput.expiryDate ? new Date(attachmentInput.expiryDate) : null}
        onChange={(date) => setAttachmentInput({ ...attachmentInput, expiryDate: formatDateForInput(date ?? undefined) })}
      />
      <Input
        label={t("Reminder Days")}
        type="number"
        value={attachmentInput.reminderDays}
        onChange={(e) => setAttachmentInput({ ...attachmentInput, reminderDays: parseInt(e.target.value) })}
      />
      <Select
        label={t("Attachment Type")}
        options={attachmentTypes.map(at => ({ label: at.attachmentType, value: at.id }))}
        value={attachmentInput.companyAttachmentTypeId}
        onChange={(v: any) => {
          const selected = attachmentTypes.find(at => at.id === v);
          setAttachmentInput({ ...attachmentInput, companyAttachmentTypeId: v, reminderDays: selected?.reminderDays });
        }}
        searchable
      />
      <Button variant="default" fullWidth className="mt-4" asChild>
        <label className="cursor-pointer">
          {t("Upload File")}
          <input
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              if (file) {
                const { valid, maxMB } = validateFileSize(file, 'userDocumentMaxSizeMB');
                if (!valid) {
                  setFileSizeError(t('fileSizeExceedsLimit', { name: file.name, maxMB }));
                  e.target.value = '';
                  return;
                }
                setFileSizeError(null);
              }
              setAttachmentInput({ ...attachmentInput, file });
            }}
          />
        </label>
      </Button>
      {fileSizeError && (
        <p className="text-sm text-destructive mt-2">
          {fileSizeError}
        </p>
      )}
      {attachmentInput.file && (
        <p className="text-sm text-muted-foreground mt-2">
          {t("Selected file")}: {attachmentInput.file.name}
        </p>
      )}
    </form>
  );
};

export default AttachmentInputForm;
